import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const clsx = (...xs) => xs.filter(Boolean).join(" ");

const MEDAL_KEYS = ["01", "03", "05", "10", "15", "21", "25", "30", "42"];

/** Médaille de palier réaliste (PNG générés côté backend). value = "01" | … | "KM42". */
export function Medal({ value = "01", size = 64, className = "", testID }) {
  const key = String(value).toUpperCase().replace("KM", "").padStart(2, "0");
  const safe = MEDAL_KEYS.includes(key) ? key : "01";
  return (
    <img
      src={`/assets/images/medals/km${safe}.png`}
      alt={`Médaille KM${safe}`}
      data-testid={testID}
      className={clsx("object-contain select-none", className)}
      style={{ width: size, height: size }}
      draggable={false}
    />
  );
}

const VARIANTS = {
  display: "font-display font-extrabold text-[34px] leading-[40px]",
  title: "font-display font-extrabold text-[26px] leading-[32px]",
  subtitle: "font-display font-bold text-[20px] leading-[26px]",
  body: "font-text text-[17px] leading-[25px]",
  bodyStrong: "font-text font-bold text-[17px] leading-[25px]",
  caption: "font-text text-[14px] leading-[20px]",
  label: "font-text font-bold text-[13px] leading-4 tracking-[0.6px] uppercase",
};

export function AppText({ variant = "body", className = "", style, children, as: Tag = "div", testID, ...rest }) {
  return (
    <Tag data-testid={testID} className={clsx(VARIANTS[variant], "text-km-ink", className)} style={style} {...rest}>
      {children}
    </Tag>
  );
}

export function Pastille({ value = "01", size = 44, tone = "brand" }) {
  const bg = tone === "sage" ? "bg-km-sage" : tone === "ink" ? "bg-km-ink" : "bg-km-coral";
  const fontSize = String(value).length > 1 ? size * 0.4 : size * 0.5;
  return (
    <div
      aria-label={`KM${value}`}
      className={clsx("flex items-center justify-center rounded-full text-white font-display font-extrabold", bg)}
      style={{ width: size, height: size, fontSize }}
    >
      {value}
    </div>
  );
}

export function Logo({ size = 36 }) {
  return (
    <div className="flex items-center gap-1">
      <Medal size={size * 1.2} />
      <span className="font-display font-extrabold tracking-tight text-km-ink" style={{ fontSize: size * 0.66 }}>
        KM<span className="text-km-coral">01</span>
      </span>
    </div>
  );
}

export function Sticker({ text, tone = "sunny", tilt = -3, className = "", testID }) {
  const map = {
    sunny: "bg-km-sunny text-km-ink",
    brand: "bg-km-coral text-white",
    sage: "bg-km-sage text-white",
    ink: "bg-km-ink text-km-cream",
  };
  return (
    <span
      data-testid={testID}
      className={clsx("inline-block self-start rounded-md px-3 py-[7px] font-display font-bold text-[14px] tracking-[0.2px] shadow-soft", map[tone], className)}
      style={{ transform: `rotate(${tilt}deg)` }}
    >
      {text}
    </span>
  );
}

export function Button({ title, children, onClick, variant = "primary", disabled, loading, icon, className = "", testID, type = "button", size = "lg" }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-full font-text font-bold text-center transition-transform active:scale-[0.96] disabled:opacity-50 disabled:pointer-events-none";
  const sizes = size === "md" ? "min-h-[48px] px-4 text-[16px]" : "min-h-[56px] px-6 text-[18px]";
  const variants = {
    primary: "bg-km-coral text-white shadow-coral hover:brightness-105",
    sage: "bg-km-sage text-white hover:brightness-105",
    secondary: "bg-white text-km-ink border-2 border-km-borderstrong hover:bg-km-tertiary",
    ghost: "bg-transparent text-km-coral hover:bg-km-coralt",
  };
  return (
    <button type={type} data-testid={testID} onClick={onClick} disabled={disabled || loading}
      className={clsx(base, sizes, variants[variant], className)}>
      {loading ? (
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <>{icon}{title || children}</>
      )}
    </button>
  );
}

export function Card({ children, className = "", style, testID, onClick }) {
  return (
    <div data-testid={testID} onClick={onClick} style={style}
      className={clsx("bg-white rounded-km border border-km-border p-6 shadow-soft", className)}>
      {children}
    </div>
  );
}

export function Badge({ text, tone = "brand" }) {
  const map = {
    brand: "bg-km-coralt text-km-oncoralt",
    sage: "bg-km-sage text-white",
    neutral: "bg-km-tertiary text-km-ontertiary",
  };
  return <span className={clsx("inline-block self-start rounded-full px-3 py-1.5 font-text font-bold text-[13px]", map[tone])}>{text}</span>;
}

export function Divider({ className = "" }) {
  return <div className={clsx("h-px bg-km-divider my-4", className)} />;
}

export function Header({ title, right, onBack }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center min-h-[56px] px-4">
      <button onClick={onBack || (() => navigate(-1))} data-testid="back-button"
        className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-km-tertiary transition-colors">
        <ArrowLeft size={22} strokeWidth={2.5} className="text-km-ink" />
      </button>
      <div className="flex-1 flex justify-center">
        {title ? <AppText variant="subtitle">{title}</AppText> : <Logo size={28} />}
      </div>
      <div className="w-11 flex justify-end">{right}</div>
    </div>
  );
}

export function Section({ title, children, className = "" }) {
  return (
    <section className={clsx("mt-12", className)}>
      <div className="w-10 h-1.5 rounded-full bg-km-coral mb-2" />
      <AppText variant="title" className="mb-4">{title}</AppText>
      {children}
    </section>
  );
}

/** Toggle switch présentational (le parent gère le clic). */
export function Toggle({ checked, testID }) {
  return (
    <span role="switch" aria-checked={checked} data-testid={testID}
      className={clsx("relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors", checked ? "bg-km-sage" : "bg-km-borderstrong")}>
      <span className={clsx("inline-block h-5 w-5 transform rounded-full bg-white transition-transform", checked ? "translate-x-6" : "translate-x-1")} />
    </span>
  );
}

export { clsx };
